import { createRoot } from 'react-dom/client'

import style from '@/embed/assets/index.scss?inline'
import LazyEmbedSidebar from '@/embed/components/LazyEmbedSidebar'

export interface EmbedProps {
  accessToken: string
  containingElementId: string
  clientId?: string
}

interface SidebarableWindow extends Window {
  _rq_sidebar_mounted?: boolean
}

export default new (class EmbedService {
  private currentScriptHostname: string | null = null

  /**
   * Return null unless autoMount has been called - i.e. we're in an embed
   */
  public getCurrentScriptHostname(): string | null {
    return this.currentScriptHostname
  }

  public autoMount(Component: React.FC<EmbedProps>) {
    if (!document.currentScript) {
      console.error('No current script found for auto-mounting')

      return
    }

    const scripElem = document.currentScript as HTMLScriptElement
    const target = scripElem.dataset.target

    if (!target) {
      console.error('No data-target attribute found for auto-mounting')

      return
    }

    // We'll want to reference this later
    this.currentScriptHostname = new URL(scripElem.src).hostname

    this.mount(target, Component)
  }

  public isInIframe() {
    try {
      return window.self !== window.top
    } catch (e) {
      // Cross-origin iframes may throw a security error when accessing window.top
      return true
    }
  }

  private mount(
    widgetElementId: string,
    Component: React.FC<EmbedProps>,
    accessToken: string | null = null,
  ) {
    const el = document.getElementById(widgetElementId)

    if (!el) {
      console.error(`Element #${widgetElementId} not found`)

      return
    }

    const finalAccessToken = accessToken || el.dataset.access_token || ''

    // if (!finalAccessToken) {
    //   console.error(`Access token not found for ${widgetElementId}`)

    //   return
    // }

    // Create shadow DOM
    const shadowRoot = el.attachShadow({ mode: 'open' })

    const styleEl = document.createElement('style')
    styleEl.textContent = style
    shadowRoot.appendChild(styleEl)

    // Create root from shadow DOM instead of element
    const root = createRoot(shadowRoot)

    const props = {
      accessToken: finalAccessToken,
      containingElementId: widgetElementId,
      clientId: el.dataset.clientid ?? undefined,
    }

    root.render(<Component {...props} />)

    // Whatever we're doing, mount the sidebar too
    this.mountSidebar(finalAccessToken)
  }

  private mountSidebar(accessToken: string) {
    const _window = window as SidebarableWindow

    if (_window._rq_sidebar_mounted) {
      // Already mounted (or mounting is in progress).
      // There can be many widgets on one page, but we only want one sidebar
      return
    }

    _window._rq_sidebar_mounted = true

    const sidebarContainerEl = document.createElement('div')
    sidebarContainerEl.id = 'rq-sidebar-container'
    document.body.appendChild(sidebarContainerEl)

    this.mount(sidebarContainerEl.id, LazyEmbedSidebar, accessToken)
  }
})()
